import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchMe, login as apiLogin, logout as apiLogout } from "../api/auth";
import { clearCompanyCache } from "../api/useMyCompany";
import { clearAttendanceCache } from "../hooks/useAttendanceData";
import { setToken } from "../api/client";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          setUser(await fetchMe());
          setReady(true);
          return;
        } catch (error) {
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            setToken(null);
            break;
          }
          // network/server glitch (cold PWA start): keep the tokens and retry once
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }
      setUser(false);
      setReady(true);
    })();
  }, []);

  const signIn = useCallback(async (email, password) => {
    const profile = await apiLogin(email, password);
    setUser(profile);
    return profile;
  }, []);

  const signOut = useCallback(async () => {
    await apiLogout();
    clearCompanyCache();
    clearAttendanceCache();
    setUser(false);
  }, []);

  const patchUser = useCallback((patch) => setUser((prev) => ({ ...prev, ...patch })), []);

  return (
    <AuthContext.Provider value={{ user, ready, signIn, signOut, patchUser }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
